<script setup>
import SectionTitle from "@/components/reusable/SectionTitle.vue";
import { Icon } from "@iconify/vue";
import { inject } from "vue";

const themeState = inject("themeState");

const primaryEducation = {
  id: 1,
  degree: "BSc in Computer Science and Engineering (CSE)",
  institution: "Dhaka International University",
  duration: "2024 - Present",
  accent: "emerald",
};

const secondaryEducation = [
  {
    id: 2,
    degree: "Diploma in Mechanical Engineering",
    institution: "Dinajpur Polytechnic Institute",
    duration: "2019 - 2023",
    accent: "blue",
  },
  {
    id: 3,
    degree: "SSC, Science",
    institution: "Khanpur High School",
    duration: "",
    accent: "violet",
  },
];

const accentClasses = {
  emerald: {
    border: "#10b981",
    icon: "text-emerald-500",
  },
  blue: {
    border: "#3b82f6",
    icon: "text-blue-500",
  },
  violet: {
    border: "#8b5cf6",
    icon: "text-violet-500",
  },
};

const getBackgroundColor = (color) => {
  if (themeState.theme === "light") {
    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.05)`;
  }

  return "#1E3851";
};
</script>

<template>
  <section class="pt-14 md:pt-16 pb-10 md:pb-12">
    <SectionTitle
      title="Education"
      desc="My academic background and learning journey"
    />

    <div class="mt-14 md:mt-16 space-y-6 md:space-y-8">
      <article
        :style="`background-color: ${getBackgroundColor(
          accentClasses[primaryEducation.accent].border
        )}; border-color: ${accentClasses[primaryEducation.accent].border};`"
        class="p-6 md:p-7 rounded-md shadow-lg hover:scale-[1.01] duration-200 border-b-2 dark:bg-ternary-dark"
      >
        <div class="mb-4 flex items-center justify-between gap-4">
          <div
            class="flex items-center justify-center w-11 h-11 rounded-full bg-white shadow-md dark:bg-slate-800"
          >
            <Icon
              icon="mdi:school-outline"
              :class="['text-xl', accentClasses[primaryEducation.accent].icon]"
            />
          </div>
          <span
            v-if="primaryEducation.duration"
            class="inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-800 p-2 text-xs font-medium text-ternary-dark dark:text-ternary-light"
          >
            {{ primaryEducation.duration }}
          </span>
        </div>

        <div>
          <h3
            class="font-bold text-md md:text-lg lg:text-xl text-ternary-dark dark:text-ternary-light uppercase"
          >
            {{ primaryEducation.degree }}
          </h3>
          <p class="mt-1 italic text-gray-700 dark:text-gray-300">
            {{ primaryEducation.institution }}
          </p>
        </div>
      </article>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <article
          v-for="item in secondaryEducation"
          :key="item.id"
          :style="`background-color: ${getBackgroundColor(
            accentClasses[item.accent].border
          )}; border-color: ${accentClasses[item.accent].border};`"
          class="p-6 md:p-7 rounded-md shadow-lg hover:scale-[1.01] duration-200 h-full border-b-2 dark:bg-ternary-dark"
        >
          <div class="mb-4 flex items-center justify-between gap-4">
            <div
              class="flex items-center justify-center w-11 h-11 rounded-full bg-white shadow-md dark:bg-slate-800"
            >
              <Icon
                icon="mdi:school-outline"
                :class="['text-xl', accentClasses[item.accent].icon]"
              />
            </div>
            <span
              v-if="item.duration"
              class="inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-800 p-2 text-xs font-medium text-ternary-dark dark:text-ternary-light"
            >
              {{ item.duration }}
            </span>
          </div>

          <div>
            <h3
              class="font-bold text-md md:text-lg lg:text-xl text-ternary-dark dark:text-ternary-light uppercase"
            >
              {{ item.degree }}
            </h3>
            <p class="mt-1 italic text-gray-700 dark:text-gray-300">
              {{ item.institution }}
            </p>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
